/* Day-Of Kit page behaviour. Custom elements own their lifecycle, so theme-editor
   re-renders (section load/unload/reorder) initialise and clean up without a page reload. */
const reduce = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
let standardEvents;
const getStandardEvents = () => (standardEvents ??= import('@shopify/events').catch(() => null));

class Base extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    this.on = (target, type, fn, opts) => target && target.addEventListener(type, fn, { signal: this.ac.signal, ...opts });
    this.setup();
    this.setAttribute('data-ready', '');
  }
  disconnectedCallback() {
    this.ac && this.ac.abort();
    this.teardown && this.teardown();
    this.removeAttribute('data-ready');
  }
}

/* ---------- gallery ---------- */
class Gallery extends Base {
  setup() {
    this.track = this.querySelector('[data-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.thumbs = this.querySelector('[data-thumbs]');
    this.pos = this.querySelector('[data-pos]');
    this.dialog = this.querySelector('dialog');
    this.index = 0;
    this.dataset.count = String(this.slides.length);
    if (!this.slides.length) return;
    if (this.thumbs) {
      this.thumbs.replaceChildren(...this.slides.map((slide, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'dok-gal__thumb';
        b.setAttribute('aria-label', `${this.dataset.lShow || ''} ${i + 1}${slide.dataset.label ? ': ' + slide.dataset.label : ''}`.trim());
        const media = slide.querySelector('.dok-ms');
        if (media && !media.classList.contains('dok-ms--filled')) {
          const mark = document.createElement('span');
          mark.className = 'dok-ms';
          mark.textContent = slide.dataset.short || String(i + 1);
          mark.style.setProperty('--ratio', '1/1');
          b.append(mark);
        } else if (media) {
          const copy = media.cloneNode(true);
          copy.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
          copy.querySelectorAll('video').forEach((v) => { v.removeAttribute('controls'); v.removeAttribute('autoplay'); v.muted = true; v.tabIndex = -1; });
          copy.style.setProperty('--ratio', '1/1');
          b.append(copy);
        }
        this.on(b, 'click', () => this.show(i));
        return b;
      }));
    }
    this.on(this.querySelector('[data-prev]'), 'click', () => this.show(this.index - 1));
    this.on(this.querySelector('[data-next]'), 'click', () => this.show(this.index + 1));
    this.on(this.querySelector('[data-zoom]'), 'click', () => this.zoom());
    this.on(this.track, 'scroll', () => {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.mark(Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth))));
    }, { passive: true });
    if (this.dialog) {
      this.on(this.dialog.querySelector('[data-close]'), 'click', () => this.dialog.close());
      this.on(this.dialog, 'click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    }
    this.mark(0);
  }
  teardown() { if (this.thumbs) this.thumbs.replaceChildren(); }
  mark(i) {
    const n = this.slides.length;
    this.index = Math.max(0, Math.min(n - 1, i));
    if (this.pos) this.pos.textContent = `${this.index + 1} / ${n}`;
    if (this.thumbs) Array.from(this.thumbs.children).forEach((b, k) => {
      if (k === this.index) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
  }
  show(i, instant) {
    const n = this.slides.length;
    if (!n) return;
    const next = (i + n) % n;
    this.track.scrollTo({ left: next * this.track.clientWidth, behavior: instant || reduce() ? 'auto' : 'smooth' });
    this.mark(next);
  }
  reveal(el) {
    const i = this.slides.findIndex((s) => s === el || s.contains(el));
    if (i > -1) this.show(i, true);
  }
  zoom() {
    const slide = this.slides[this.index];
    const media = slide && slide.querySelector('.dok-ms');
    if (!this.dialog || !media) return;
    const copy = media.cloneNode(true);
    copy.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    this.dialog.querySelector('[data-body]').replaceChildren(copy);
    this.dialog.querySelector('[data-title]').textContent = slide.dataset.label || `${this.index + 1} / ${this.slides.length}`;
    if (this.dialog.showModal) this.dialog.showModal(); else this.dialog.setAttribute('open', '');
  }
}

/* ---------- carousel ---------- */
class Carousel extends Base {
  setup() {
    this.track = this.querySelector('[data-track]');
    this.cards = this.track ? Array.from(this.track.children) : [];
    this.dotsEl = this.querySelector('[data-dots]');
    this.status = this.querySelector('[data-status]');
    this.current = -1;
    if (!this.cards.length) { this.setAttribute('data-static', ''); return; }
    this.on(this.querySelector('[data-prev]'), 'click', () => this.go(this.current - 1, true));
    this.on(this.querySelector('[data-next]'), 'click', () => this.go(this.current + 1, true));
    if (this.dotsEl) {
      this.dotsEl.replaceChildren(...this.cards.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'dok-car__dot';
        b.setAttribute('aria-label', `${this.dataset.lGoto || ''} ${i + 1} / ${this.cards.length}`.trim());
        b.append(document.createElement('span'));
        this.on(b, 'click', () => this.go(i, true));
        return b;
      }));
    }
    this.on(this.track, 'scroll', () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.sync()); }, { passive: true });
    if ('ResizeObserver' in window) { this.ro = new ResizeObserver(() => this.sync()); this.ro.observe(this.track); }
    const seconds = Number(this.dataset.autoplay || 0);
    if (seconds > 0 && !reduce()) {
      this.on(this, 'pointerenter', () => this.pause());
      this.on(this, 'pointerleave', () => this.resume());
      this.on(this, 'focusin', () => this.pause());
      this.on(this, 'focusout', () => this.resume());
      this.on(document, 'visibilitychange', () => (document.hidden ? this.pause() : this.resume()));
      this.seconds = seconds;
      this.resume();
    }
    this.sync();
  }
  teardown() { this.pause(); this.ro && this.ro.disconnect(); this.dotsEl && this.dotsEl.replaceChildren(); }
  step() { return this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.cards[0].offsetWidth; }
  go(i, user) {
    const n = this.cards.length;
    const loop = this.hasAttribute('data-loop');
    let next = i;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    if (next > this.current && atEnd) next = loop ? 0 : this.current;
    if (next < 0) next = loop ? n - 1 : 0;
    if (next > n - 1) next = loop ? 0 : n - 1;
    this.track.scrollTo({ left: next * this.step(), behavior: reduce() ? 'auto' : 'smooth' });
    if (user && this.seconds) { this.pause(); this.resume(); }
  }
  sync() {
    const s = this.step();
    if (!s) return;
    const isStatic = this.track.scrollWidth <= this.track.clientWidth + 2;
    this.toggleAttribute('data-static', isStatic);
    const i = Math.max(0, Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / s)));
    const visible = this.classList.contains('dok-car--focus') ? 1 : Math.max(1, Math.round(this.track.clientWidth / s));
    if (i === this.current && visible === this.visible) return;
    this.current = i; this.visible = visible;
    this.cards.forEach((c, k) => c.classList.toggle('is-on', k === i));
    if (this.dotsEl) Array.from(this.dotsEl.children).forEach((d, k) => {
      d.classList.toggle('is-on', k >= i && k < i + visible);
      if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
    });
    if (this.status) this.status.textContent = `${i + 1} / ${this.cards.length}`;
  }
  pause() { clearInterval(this.timer); this.timer = null; }
  resume() { if (this.seconds && !this.timer && !this.locked) this.timer = setInterval(() => this.go(this.current + 1), this.seconds * 1000); }
  reveal(el) {
    const i = this.cards.findIndex((c) => c === el || c.contains(el));
    this.locked = true; this.pause();
    if (i > -1) this.track.scrollTo({ left: i * this.step(), behavior: 'auto' });
  }
  release() { this.locked = false; this.resume(); }
}

/* ---------- accordion ---------- */
class Accordion extends Base {
  setup() {
    const rows = () => Array.from(this.children).filter((c) => c.tagName === 'DETAILS');
    if (this.dataset.initial === 'first' && rows()[0]) rows()[0].open = true;
    if (this.dataset.initial === 'all') rows().forEach((r) => { r.open = true; });
    this.on(this, 'toggle', (e) => {
      if (!this.hasAttribute('data-single') || !e.target.open || e.target.parentElement !== this) return;
      rows().forEach((r) => { if (r !== e.target) r.open = false; });
    }, { capture: true });
  }
}

/* ---------- marquee ---------- */
class Marquee extends Base {
  setup() {
    const track = this.querySelector('[data-track]');
    const group = track && track.firstElementChild;
    if (!group || !group.children.length || reduce()) return;
    const originals = Array.from(group.children);
    let guard = 0;
    while (group.scrollWidth < this.clientWidth && guard++ < 30) {
      originals.forEach((n) => { const c = n.cloneNode(true); c.setAttribute('aria-hidden', 'true'); c.removeAttribute('data-shopify-editor-block'); c.dataset.clone = ''; group.append(c); });
    }
    const copy = group.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.dataset.clone = '';
    copy.querySelectorAll('[data-shopify-editor-block]').forEach((n) => n.removeAttribute('data-shopify-editor-block'));
    copy.querySelectorAll('a').forEach((a) => { a.tabIndex = -1; });
    track.append(copy);
    this.track = track;
  }
  teardown() { this.querySelectorAll('[data-clone]').forEach((n) => n.remove()); }
}

/* ---------- variant picker ---------- */
class Variants extends Base {
  setup() {
    const data = this.querySelector('script[type="application/json"]');
    try { this.variants = JSON.parse(data ? data.textContent : '[]'); } catch (e) { this.variants = []; }
    this.on(this, 'change', () => this.update(true));
    this.update(false);
  }
  selection() { return Array.from(this.querySelectorAll('fieldset')).map((f) => { const c = f.querySelector('input:checked'); return c ? c.value : null; }); }
  update(announce) {
    const sel = this.selection();
    const variant = this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
    this.querySelectorAll('fieldset').forEach((f, idx) => {
      f.querySelectorAll('input').forEach((input) => {
        const ok = this.variants.some((v) => v.available && v.options[idx] === input.value && v.options.every((o, i) => i === idx || o === sel[i]));
        input.classList.toggle('is-off', !ok);
      });
    });
    if (!announce) return;
    if (variant && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    document.dispatchEvent(new CustomEvent('dok:variant', { detail: { productId: this.dataset.productId, variant } }));
  }
}

/* ---------- buy button ---------- */
class Buy extends Base {
  setup() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.error = this.querySelector('[data-error]');
    this.on(this.form, 'submit', (e) => this.submit(e));
    this.on(document, 'dok:variant', (e) => {
      if (String(e.detail.productId) !== String(this.dataset.productId) || !this.button) return;
      const v = e.detail.variant;
      const input = this.form && this.form.querySelector('[name="id"]');
      if (input && v) input.value = v.id;
      this.button.disabled = !v || !v.available;
      const label = this.button.querySelector('[data-label]');
      if (label) label.textContent = !v ? this.dataset.lUnavailable : v.available ? this.dataset.lDefault : this.dataset.lSoldout;
      this.fail('');
    });
  }
  fail(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }
  async submit(e) {
    if (!window.fetch || !this.dataset.addUrl) return;
    e.preventDefault();
    if (this.busy) return;
    this.busy = true;
    this.fail('');
    this.button.classList.add('is-loading');
    this.button.setAttribute('aria-busy', 'true');
    const body = new FormData(this.form);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component')).map((c) => c.dataset.sectionId).filter(Boolean);
    if (sectionIds.length) body.append('sections', sectionIds.join(','));
    const std = await getStandardEvents();
    const hasThemeCart = !!(std && std.CartLinesUpdateEvent && document.querySelector('cart-items-component, cart-drawer-component, cart-icon'));
    let deferred = null;
    const readCart = async () => (await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })).json();
    try {
      if (hasThemeCart) {
        deferred = std.CartLinesUpdateEvent.createPromise();
        deferred.promise.catch(() => {});
        this.dispatchEvent(new std.CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: [{ merchandiseId: String(body.get('id')), quantity: Number(body.get('quantity')) || 1 }],
          promise: deferred.promise,
        }));
      }
      const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body, credentials: 'same-origin' });
      const json = await response.json();
      if (!response.ok || json.status) throw new Error(json.description || json.message || this.dataset.lError || '');
      if (!hasThemeCart) { window.location.assign(this.dataset.cartUrl); return; }
      const cart = await readCart();
      deferred.resolve({
        cart: std.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'dok-buy', sourceId: this.id, itemCount: 1, productId: this.dataset.productId, sections: json.sections, didError: false },
      });
    } catch (err) {
      this.fail((err && err.message) || this.dataset.lError || '');
      if (deferred) {
        try {
          const cart = await readCart();
          deferred.resolve({ cart: std.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'dok-buy', didError: true } });
        } catch (inner) { deferred.reject(err); }
      }
    } finally {
      this.busy = false;
      this.button.classList.remove('is-loading');
      this.button.removeAttribute('aria-busy');
    }
  }
}

define('dok-gallery', Gallery);
define('dok-carousel', Carousel);
define('dok-accordion', Accordion);
define('dok-marquee', Marquee);
define('dok-variants', Variants);
define('dok-buy', Buy);

/* ---------- page-level wiring (registered once per page) ---------- */
if (!window.__dokWired) {
  window.__dokWired = true;

  document.addEventListener('dok:variant', (e) => {
    const { productId, variant } = e.detail;
    document.querySelectorAll(`[data-dok-price][data-product-id="${productId}"]`).forEach((el) => {
      el.innerHTML = variant ? `${el.hasAttribute('data-compare') && variant.compare ? `<s>${variant.compare}</s>` : ''}${variant.price}` : '';
    });
  });

  const openTarget = (hash) => {
    if (!hash || hash.length < 2) return;
    let el = null;
    try { el = document.getElementById(decodeURIComponent(hash.slice(1))); } catch (e) { return; }
    for (let n = el; n; n = n.parentElement) if (n.tagName === 'DETAILS') n.open = true;
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href*="#"]');
    if (a && a.pathname === window.location.pathname) openTarget(a.hash);
  });
  window.addEventListener('hashchange', () => openTarget(window.location.hash));
  openTarget(window.location.hash);

  /* Theme editor: reveal the selected block even when it sits in a closed row or off-screen slide. */
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    for (let n = el; n; n = n.parentElement) if (n.tagName === 'DETAILS') n.open = true;
    const car = el.closest('dok-carousel');
    if (car && car.reveal) car.reveal(el);
    const gal = el.closest('dok-gallery');
    if (gal && gal.reveal) gal.reveal(el);
    const mq = el.closest('dok-marquee');
    if (mq) mq.classList.add('is-paused');
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const car = e.target.closest('dok-carousel');
    if (car && car.release) car.release();
    const mq = e.target.closest('dok-marquee');
    if (mq) mq.classList.remove('is-paused');
  });
}
